/**
 * F12 Section 5.2 — Admin venue-photo review / decision page.
 *
 * Route: /venue-photos/:photoId
 * Permission: venue_photos:moderate
 *
 *   Left column (≈60%): full-size photo display using `variants.heroUrl` and
 *                       falling back to `cdnUrl` for legacy / pre-Sharp rows.
 *   Right column (≈40%): venue context + photo metadata.
 *   Bottom:              Approve (primary) + Reject (destructive) for pending
 *                        photos. For approved/rejected: a metadata block with
 *                        the decision history and the buttons are hidden.
 *
 * The API does not expose a single-photo detail route; we use `findOne` to
 * walk the three status queues until the requested id surfaces, then render
 * exactly the row carried by the queue (photo + venue context).
 *
 * Approve flow: Dialog confirm → POST /approve → toast → navigate back.
 * Reject flow:  Modal with `<RejectionReasonPicker>` + min-10-char validation
 *               → POST /reject → toast → navigate back.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  Modal,
  RejectionReasonPicker,
  Spinner,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { VenuePhotoModerationStatus } from '@bravemove/types';
import { adminVenuePhotoApi, type VenuePhotoQueueItem } from '../../api/venue-photo.js';
import { translateApiError } from '../../auth/errors.js';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}

const STATUS_TONE: Record<VenuePhotoModerationStatus, 'success' | 'warning' | 'danger'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

const STATUS_LABEL_KEY: Record<VenuePhotoModerationStatus, string> = {
  approved: 'admin:venue-editor.queue.filterApproved',
  pending: 'admin:venue-editor.queue.filterPending',
  rejected: 'admin:venue-editor.queue.filterRejected',
};

// ─── Reject dialog ───────────────────────────────────────────────────────────

interface RejectPreset {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
}

function RejectModal({
  open,
  onClose,
  onSuccess,
  submitting,
  serverError,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (reason: string) => void;
  submitting: boolean;
  serverError: string | null;
}) {
  const { t } = useI18n();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [freeformText, setFreeformText] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedId(null);
    setFreeformText('');
    setLocalError(null);
  }, [open]);

  const presets: ReadonlyArray<RejectPreset> = [
    {
      id: 'inappropriate_content',
      label: t('admin:venue-editor.rejectionReasons.inappropriate_content'),
    },
    {
      id: 'low_quality',
      label: t('admin:venue-editor.rejectionReasons.low_quality'),
    },
    {
      id: 'wrong_venue',
      label: t('admin:venue-editor.rejectionReasons.wrong_venue'),
    },
    {
      id: 'copyright',
      label: t('admin:venue-editor.rejectionReasons.copyright'),
    },
    {
      id: 'other',
      label: t('admin:venue-editor.rejectionReasons.other'),
    },
  ];

  const composedReason = (() => {
    const preset = presets.find((p) => p.id === selectedId);
    const head = preset ? preset.label : '';
    return [head, freeformText.trim()].filter(Boolean).join(' — ');
  })();

  const handleSubmit = () => {
    if (composedReason.trim().length < 10) {
      setLocalError(t('admin:venue-editor.review.rejectReasonTooShort'));
      return;
    }
    setLocalError(null);
    onSuccess(composedReason);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:venue-editor.review.rejectDialogTitle')}
      size="md"
      footer={
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          {t('admin:venue-editor.review.cancel')}
        </Button>
      }
    >
      <p className="text-[13px] text-ink-2 leading-snug mb-3">
        {t('admin:venue-editor.review.rejectDialogBody')}
      </p>
      {(localError ?? serverError) ? (
        <Banner tone="danger" className="mb-3">
          {localError ?? serverError}
        </Banner>
      ) : null}
      <RejectionReasonPicker
        presets={presets}
        selectedId={selectedId}
        freeformText={freeformText}
        onChange={(id, txt) => {
          setSelectedId(id);
          setFreeformText(txt);
          if (localError) setLocalError(null);
        }}
        onSubmit={handleSubmit}
        submitLabel={t('admin:venue-editor.review.rejectDialogCta')}
        freeformLabel={t('admin:venue-editor.review.decisionReasonLabel')}
        freeformPlaceholder={t('admin:venue-editor.review.rejectReasonHelper')}
        submitting={submitting}
      />
    </Modal>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function VenuePhotoReviewPage() {
  const { t, locale } = useI18n();
  const { photoId } = useParams<{ photoId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [item, setItem] = useState<VenuePhotoQueueItem | null>(null);
  const [resolvedStatus, setResolvedStatus] = useState<VenuePhotoModerationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectServerError, setRejectServerError] = useState<string | null>(null);

  const fetchPhoto = useCallback(async () => {
    if (!photoId) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const found = await adminVenuePhotoApi.findOne(photoId);
      if (!found) {
        setNotFound(true);
        setItem(null);
        setResolvedStatus(null);
      } else {
        setItem(found.row);
        setResolvedStatus(found.status);
      }
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [photoId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchPhoto();
  }, [fetchPhoto]);

  const handleApprove = async () => {
    if (!item) return;
    setApproveSubmitting(true);
    try {
      await adminVenuePhotoApi.approve(item.photo.id);
      toast.push({ tone: 'success', title: t('admin:venue-editor.review.approvedToast') });
      setApproveOpen(false);
      navigate('/venue-photos');
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!item) return;
    setRejectSubmitting(true);
    setRejectServerError(null);
    try {
      await adminVenuePhotoApi.reject(item.photo.id, reason);
      toast.push({ tone: 'success', title: t('admin:venue-editor.review.rejectedToast') });
      setRejectOpen(false);
      navigate('/venue-photos');
    } catch (err) {
      const msg = translateApiError(err, t);
      setRejectServerError(msg);
      toast.push({ tone: 'danger', title: msg });
    } finally {
      setRejectSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={<UIIcon name="eye" size={36} color="currentColor" />}
          title={t('admin:venue-editor.review.notFoundTitle')}
          description={t('admin:venue-editor.review.notFoundBody')}
        />
        <Button variant="ghost" onClick={() => navigate('/venue-photos')}>
          {t('admin:venue-editor.review.backToQueue')}
        </Button>
      </div>
    );
  }

  if (error || !item || !resolvedStatus) {
    return (
      <div className="space-y-4">
        <Banner tone="danger">{error ?? t('admin:errors.generic') ?? 'Error'}</Banner>
        <Button variant="ghost" onClick={() => navigate('/venue-photos')}>
          {t('admin:venue-editor.review.backToQueue')}
        </Button>
      </div>
    );
  }

  const { photo, venueId, venueSlug, venueName, ownerUserId } = item;
  const heroSrc = photo.variants?.heroUrl ?? photo.cdnUrl;
  const status = resolvedStatus;
  const isPending = status === 'pending';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <button
          type="button"
          className="text-[12px] text-accent hover:underline underline-offset-2"
          onClick={() => navigate('/venue-photos')}
        >
          &larr; {t('admin:venue-editor.review.backToQueue')}
        </button>
        <h1 className="text-h1 font-bold text-ink">
          {t('admin:venue-editor.pageTitle.photoReview')}
        </h1>
        <Badge tone={STATUS_TONE[status]} size="sm">
          {t(STATUS_LABEL_KEY[status])}
        </Badge>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column — full-size photo (≈60% on desktop) */}
        <div className="lg:col-span-3">
          <Card>
            <div className="rounded-md overflow-hidden bg-surface-2 border border-hairline-2">
              {heroSrc ? (
                <img
                  src={heroSrc}
                  alt={photo.altText ?? ''}
                  className="w-full max-h-[640px] object-contain bg-black/5"
                />
              ) : (
                <div className="aspect-[4/3] flex items-center justify-center text-ink-3">
                  <UIIcon name="eye" size={48} color="currentColor" />
                </div>
              )}
            </div>
            {heroSrc ? (
              <a
                href={heroSrc}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block text-[12px] text-accent hover:underline underline-offset-2"
              >
                {t('admin:venue-editor.review.openInNewTab')}
              </a>
            ) : null}
          </Card>
        </div>

        {/* Right column — venue context + photo metadata (≈40% on desktop) */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <h2 className="text-[13px] font-semibold text-ink mb-3">
              {t('admin:venue-editor.review.venueLabel')}
            </h2>
            <dl className="grid grid-cols-1 gap-y-2 text-[12.5px]">
              <div>
                <dt className="text-ink-3">{t('admin:venue-editor.review.venueLabel')}</dt>
                <dd className="text-ink font-medium">{venueName}</dd>
              </div>
              <div>
                <dt className="text-ink-3">{t('admin:venue-editor.review.venueSlugLabel')}</dt>
                <dd>
                  {/* V-22 — router Link (a raw <a> forced a full page reload) */}
                  <Link
                    to={`/venues/${venueId}`}
                    className="font-mono text-[12px] text-accent hover:underline underline-offset-2"
                  >
                    {venueSlug}
                  </Link>
                </dd>
              </div>
              <div>
                <dt className="text-ink-3">{t('admin:venue-editor.review.uploadedByLabel')}</dt>
                <dd className="font-mono text-[11.5px] text-ink-2">
                  {ownerUserId ?? t('admin:venue-editor.review.noOwnerLabel')}
                </dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h2 className="text-[13px] font-semibold text-ink mb-3">
              {t('admin:venue-editor.review.currentStatusLabel')}
            </h2>
            <dl className="grid grid-cols-1 gap-y-2 text-[12.5px]">
              <div>
                <dt className="text-ink-3">{t('admin:venue-editor.review.uploadedAtLabel')}</dt>
                <dd className="text-ink">{formatDate(photo.createdAt, locale)}</dd>
              </div>
              <div>
                <dt className="text-ink-3">{t('admin:venue-editor.review.mimeLabel')}</dt>
                <dd className="font-mono text-[11.5px] text-ink-2">{photo.mime ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-ink-3">{t('admin:venue-editor.review.sizeLabel')}</dt>
                <dd className="text-ink">{formatBytes(photo.sizeBytes)}</dd>
              </div>
              {photo.widthPx && photo.heightPx ? (
                <div>
                  <dt className="text-ink-3">{t('admin:venue-editor.review.dimensionsLabel')}</dt>
                  <dd className="text-ink">
                    {photo.widthPx} × {photo.heightPx}
                  </dd>
                </div>
              ) : null}
              {photo.altText ? (
                <div>
                  <dt className="text-ink-3">{t('admin:venue-editor.review.altTextLabel')}</dt>
                  <dd className="text-ink">{photo.altText}</dd>
                </div>
              ) : null}
            </dl>
          </Card>

          {!isPending ? (
            <Card>
              <h2 className="text-[13px] font-semibold text-ink mb-3">
                {t('admin:venue-editor.review.decisionMetadataLabel')}
              </h2>
              <dl className="grid grid-cols-1 gap-y-2 text-[12.5px]">
                <div>
                  <dt className="text-ink-3">{t('admin:venue-editor.review.decisionAtLabel')}</dt>
                  <dd className="text-ink">{formatDate(photo.updatedAt, locale)}</dd>
                </div>
              </dl>
            </Card>
          ) : null}
        </div>
      </div>

      {/* Sticky action bar */}
      {isPending ? (
        <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-bg/95 backdrop-blur border-t border-hairline-2">
          <div className="flex flex-wrap items-center gap-3 justify-end">
            <Button variant="secondary" onClick={() => setRejectOpen(true)}>
              {t('admin:venue-editor.review.rejectCta')}
            </Button>
            <Button variant="primary" onClick={() => setApproveOpen(true)}>
              {t('admin:venue-editor.review.approveCta')}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Approve dialog */}
      <Dialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={() => void handleApprove()}
        title={t('admin:venue-editor.review.approveDialogTitle')}
        confirmLabel={t('admin:venue-editor.review.approveDialogCta')}
        cancelLabel={t('admin:venue-editor.review.cancel')}
        loading={approveSubmitting}
      >
        <p className="text-[13px] text-ink-2 leading-snug">
          {t('admin:venue-editor.review.approveDialogBody')}
        </p>
      </Dialog>

      {/* Reject modal */}
      <RejectModal
        open={rejectOpen}
        onClose={() => {
          setRejectOpen(false);
          setRejectServerError(null);
        }}
        onSuccess={(reason) => void handleReject(reason)}
        submitting={rejectSubmitting}
        serverError={rejectServerError}
      />
    </div>
  );
}
